import { useCallback, useEffect, useState } from 'react';

import { QRCodeSVG } from 'qrcode.react';
import { useForm } from 'react-hook-form';
import {
  EnableMfaRequestMfaTypeEnum,
  MfaControllerApi,
} from '../../openapi/keymaker';
import ErrorService from '../../services/ErrorService';
import { fetchKeymakerCurrentUser, mfaSignIn } from '../../slices/AuthSlice';
import {
  showErrorToast,
  showSuccessToast,
} from '../../slices/ToastNotificationSlice';
import { useAppDispatch } from '../../slices/store';
import { getCommonsApiErrorMessage } from '../../utils/ErrorUtils';
import { getKeymakerConfiguration } from '../../utils/OpenapiConfigurationUtils';
import SidebarModalForm from '../Modals/SidebarModalForm';
import ControlledTextInput from '../inputs/ControlledTextInput';
import StepContentInput from './StepContentInput';

interface FormData {
  code: number;
}

interface AuthenticatorSetupFormProps {
  isOpen: boolean;
  onClose(): void;
  setIsMFASetupModalOpen(enable: boolean): void;
  isForceMfa?: boolean;
}

const AuthenticatorSetupForm: React.FC<AuthenticatorSetupFormProps> = ({
  isOpen,
  onClose,
  setIsMFASetupModalOpen,
  isForceMfa,
}) => {
  const dispatch = useAppDispatch();
  const [QRcode, setQRcode] = useState<string>();
  const {
    control,
    handleSubmit,
    setError,
    formState: { isSubmitting },
  } = useForm<FormData>();

  const fetchQRCode = useCallback(async () => {
    try {
      const { data } = await new MfaControllerApi(
        await getKeymakerConfiguration(),
      ).getAuthenticatorQrCode();
      setQRcode(data);
    } catch (e: any) {
      ErrorService.notify(
        'Unable to fetch two factor authentication qr code',
        e,
      );
      dispatch(
        showErrorToast(
          'We had a problem fetching last two factor authentication qr code',
          'Please try again in a few moments.',
        ),
      );
    }
  }, [dispatch]);

  useEffect(() => {
    fetchQRCode();
  }, [fetchQRCode]);

  const onSubmit = async (values: FormData) => {
    try {
      await new MfaControllerApi(await getKeymakerConfiguration()).enableMfa({
        code: values.code,
        mfaType: EnableMfaRequestMfaTypeEnum.Authenticator,
      });
      if (isForceMfa) {
        await dispatch(mfaSignIn(values.code));
      }
      dispatch(fetchKeymakerCurrentUser());
      setIsMFASetupModalOpen(false);
      onClose();
      dispatch(
        showSuccessToast('Successfully enabled two factor authentication.'),
      );
    } catch (e: any) {
      setError('code', {
        message: getCommonsApiErrorMessage(e),
      });
      ErrorService.notify('Unable to enable two factor authentication', e, {
        data: {
          code: values.code,
          mfaType: EnableMfaRequestMfaTypeEnum.Authenticator,
        },
      });
    }
  };

  return (
    <SidebarModalForm
      isOpen={isOpen}
      onClose={onClose}
      isSubmitting={isSubmitting}
      title='Two-Factor Authentication: Authenticator App'
      subtitle='Scan QR code to link authenticator app and get a code for verification.'
      onSubmit={handleSubmit(onSubmit)}
      actionTitle='Verify Code'
      cancelTitle='Back'
      isEqualWidth
    >
      <div>
        <StepContentInput step={1}>
          <p className='font-inter font-normal text-base'>
            Scan the QR code below
          </p>
        </StepContentInput>
        <p className='font-inter font-normal my-3 text-sm'>
          On your authenticator app, add an account to select “Scan a QR code”
        </p>
        <div className='flex flex-row justify-center'>
          <QRCodeSVG value={QRcode!} size={200} />,
        </div>
        <div className='mt-10'>
          <StepContentInput step={2}>
            <p className='font-inter font-normal text-base'>
              Enter authentication code
            </p>
          </StepContentInput>
          <p className='font-inter font-normal my-3 text-sm'>
            Enter the code shown on the authenticator app
          </p>
          <ControlledTextInput<FormData, 'code'>
            control={control}
            name='code'
            type='number'
            placeholder='123456'
            rules={{
              required: 'Please enter authentication code',
              minLength: {
                value: 6,
                message: 'Must have 6 digit number',
              },
              maxLength: {
                value: 6,
                message: 'Must have 6 digit number',
              },
            }}
          />
        </div>
      </div>
    </SidebarModalForm>
  );
};

export default AuthenticatorSetupForm;
